export const normalizeOrderedIds = (ordering = []) => {
  let values = ordering;

  if (typeof values === "string") {
    try {
      values = JSON.parse(values);
    } catch {
      values = [];
    }
  }

  if (!Array.isArray(values)) return [];

  const seen = new Set();
  return values.filter((value) => {
    if (value === null || value === undefined || String(value).trim() === "") {
      return false;
    }

    const id = String(value).trim();
    if (!/^\d+$/.test(id) || /^0+$/.test(id)) return false;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

export const upsertGuideSection = (sections = [], savedSection) => {
  if (!savedSection?.welcome_guide_question_id) return sections || [];

  const id = String(savedSection.welcome_guide_question_id);
  const exists = (sections || []).some(
    (section) => String(section.welcome_guide_question_id) === id,
  );

  if (!exists) return [savedSection, ...(sections || [])];
  return (sections || []).map((section) =>
    String(section.welcome_guide_question_id) === id
      ? { ...section, ...savedSection }
      : section,
  );
};

export const mergeGuideSectionOverrides = (
  serverSections = [],
  localOverrides = [],
) =>
  (localOverrides || []).reduce(
    (sections, section) => upsertGuideSection(sections, section),
    serverSections || [],
  );

export const mergeSavedGuideSection = (
  currentSection = {},
  submittedSection = {},
  savedSection = {},
) => ({
  ...currentSection,
  ...submittedSection,
  ...savedSection,
  attachments:
    savedSection?.attachments ??
    submittedSection?.attachments ??
    currentSection?.attachments ??
    [],
  restrictions:
    savedSection?.restrictions ??
    submittedSection?.restrictions ??
    currentSection?.restrictions ??
    [],
});

export const upsertGuideSectionOrder = (
  orders = [],
  tabType,
  sectionId,
) =>
  (orders || []).map((order) =>
    String(order.type) === String(tabType)
      ? {
          ...order,
          ordering: [
            sectionId,
            ...normalizeOrderedIds(order.ordering).filter(
              (id) => String(id) !== String(sectionId),
            ),
          ],
        }
      : {
          ...order,
          ordering: normalizeOrderedIds(order.ordering),
        },
  );

export const mergePersistedGuideSectionOrder = (
  orders = [],
  persistedOrder,
) => {
  if (!persistedOrder?.type) return orders || [];

  const normalizedOrder = {
    ...persistedOrder,
    ordering: normalizeOrderedIds(persistedOrder.ordering),
  };
  const exists = (orders || []).some(
    (order) => String(order.type) === String(persistedOrder.type),
  );

  if (!exists) return [...(orders || []), normalizedOrder];
  return (orders || []).map((order) =>
    String(order.type) === String(persistedOrder.type)
      ? { ...order, ...normalizedOrder }
      : order,
  );
};

export const mergeCreatedGuideSectionOrder = (
  orders = [],
  tabType,
  sectionId,
  persistedOrder,
) => {
  const mergedOrders = persistedOrder
    ? mergePersistedGuideSectionOrder(orders, persistedOrder)
    : orders || [];

  // The newly created block must be present in local ordering immediately.
  // This also protects the UI when a successful API response contains an
  // older welcome_order snapshot while the persisted data catches up.
  return upsertGuideSectionOrder(mergedOrders, tabType, sectionId);
};

export const selectOrderedGuideSections = (sections = [], ordering = []) =>
  normalizeOrderedIds(ordering)
    .map((id) =>
      (sections || []).find(
        (section) =>
          String(id) === String(section.welcome_guide_question_id) ||
          String(id) === String(section.parent_id),
      ),
    )
    .filter(Boolean);

export const removeGuideSection = (sections = [], sectionId) =>
  (sections || []).filter(
    (section) =>
      String(section.welcome_guide_question_id) !== String(sectionId),
  );
